// Small form controls shared across the NEXOVA admin screens.
//
// The repo's shadcn set doesn't (yet) ship a Select / Tabs / Radio primitive,
// so these are thin, token-styled native controls. Kept dependency-flat and
// consistent with the nexus Input chrome.

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// A native <select> styled to match the nexus Input.
export function NativeSelect({
  className,
  children,
  ...props
}: React.ComponentProps<"select">) {
  return (
    <select
      className={cn(
        "h-8 w-full rounded-lg border border-nx-border bg-nx-bg-2 px-2.5 text-sm text-nx-text outline-none focus-visible:border-nx-brand focus-visible:ring-3 focus-visible:ring-nx-brand/20",
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
}

// Offset pager, mirroring the AuditPage Prev/Next pattern: Prev is disabled at
// offset 0; Next is disabled when the CURRENT fetched page came back short of
// pageSize (no further rows can exist). `count` must be the UNFILTERED row
// count of the last fetch — a client-side "hide terminal" filter must not
// disable Next early.
export function Pager({
  offset,
  pageSize,
  count,
  loading,
  onOffset,
}: {
  offset: number;
  pageSize: number;
  count: number;
  loading?: boolean;
  onOffset: (next: number) => void;
}) {
  return (
    <div className="mt-3 flex justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={offset === 0 || loading}
        onClick={() => onOffset(Math.max(0, offset - pageSize))}
      >
        Prev
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={count < pageSize || loading}
        onClick={() => onOffset(offset + pageSize)}
      >
        Next
      </Button>
    </div>
  );
}

// A segmented control over plain buttons (no shadcn Tabs dependency).
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: React.ReactNode }[];
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex rounded-lg border border-nx-border bg-nx-bg-2 p-0.5",
        className,
      )}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-3 py-1 text-sm font-medium transition-colors",
            value === o.value
              ? "bg-nx-surface text-nx-text shadow-sm"
              : "text-nx-text-3 hover:text-nx-text-2",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
